import { useCallback, useContext, useState } from 'react'

import { typography } from '@joor/design-system'
import { FileError, useDropzone } from 'react-dropzone'
import styled from 'styled-components'

import { ImportOrdersContext } from '../../ImportOrders.context'
import { MAX_FILE_SIZE, SimplifiedTypes } from '../FirstStep.utils'

const DropContainer = styled.div`
  min-height: 84px;
  border: 1px dashed var(--color-primary-400);
  border-radius: var(--size-border-radius-small);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-sizing: unset;
  &:hover {
    border: 1px dashed var(--color-secondary-800);
  }
  ${typography.alpha.primaryActive.body3}

  &.disabled {
    cursor: not-allowed;
    background: transparent;
    opacity: 0.6;
  }

  > div {
    text-align: center;

    > p span {
      text-align: center;
    }
  }
`

const DnDDescription = styled.p`
  text-align: center;
  color: var(--color-primary-400);
  display: flex;
  flex-direction: column;
  margin: 0;
`

const BrowseTitle = styled.span`
  ${typography.alpha.primaryActive.body3}
  color: var(--color-secondary-800);

  &.disabled {
    color: var(--color-primary-400);
    background: transparent;
  }
`

const CaptionTextContainer = styled.span`
  font-size: 10px;
  color: var(--color-primary-400);
  margin: 0 0 8px;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: transparent;
  }
`

type Props = {
  onFileSelected: (file: File[], errors?: FileError[]) => Promise<void>
}

const Dropzone = ({ onFileSelected }: Props): JSX.Element => {
  const [dropzoneDisabled, setDropzoneDisabled] = useState<boolean>(false)

  const { templateType } = useContext(ImportOrdersContext)

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      if (acceptedFiles.length > 0 || fileRejections?.length > 0) {
        onFileSelected(
          acceptedFiles,
          fileRejections?.[0]?.errors as FileError[],
        )
      }
    },
    [onFileSelected],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept:
      'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv',
    maxSize: MAX_FILE_SIZE,
    maxFiles: templateType?.value === SimplifiedTypes.Standard ? 100 : 1,
    disabled: !templateType,
  })

  const handleDropContainerClick = () => {
    setDropzoneDisabled(true)
    setTimeout(() => {
      setDropzoneDisabled(false)
    }, 500)
  }

  return (
    <>
      <DropContainer
        className={!templateType ? 'disabled' : ''}
        {...getRootProps()}
      >
        <input
          {...getInputProps()}
          disabled={dropzoneDisabled}
          onClick={handleDropContainerClick}
        />
        {isDragActive ? (
          <p>Drag and Drop files here ...</p>
        ) : (
          <div>
            <DnDDescription>
              <span>Drag and Drop files here...</span>
              <span> or</span>
              <BrowseTitle className={!templateType ? 'disabled' : ''}>
                Browse files
              </BrowseTitle>
            </DnDDescription>
          </div>
        )}
      </DropContainer>
      <CaptionTextContainer className={!templateType ? 'disabled' : ''}>
        Maximum allowed size is 4MB. Only Excel or CSV files
      </CaptionTextContainer>
    </>
  )
}

export default Dropzone
